import React, { useState, useEffect } from 'react';
import ErpTemplate from '../../components/ErpTemplate';
import { FaSave, FaPlus, FaTimes, FaUsers, FaPen, FaTrash, FaExclamationCircle } from 'react-icons/fa';
import api from '../../services/api';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FinField, FinInput, FinCheck, FinChecks } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import AlertaSedes, { RedesRelacionadas } from '../../components/AlertaSedes';
import '../../css/financeiro-parcela-pagar.css';

export default function CadastroFornecedorGrupo({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const [dados, setDados] = useState([]);
    // Fornecedores do grupo. Adicionar e remover mexem só nesta lista: nada vai ao banco
    // antes do Salvar, que manda a lista inteira.
    const [fornecedores, setFornecedores] = useState([]);
    const [adicionando, setAdicionando] = useState(false);
    const [selecionado, setSelecionado] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);
    // A leitura do grupo falhou: salvar mandaria a lista vazia e tiraria todos da rede.
    const [erroCarga, setErroCarga] = useState(false);
    // Modal das sedes: abre quando a rede passa a ter mais de uma, e pelo (!) quando já tem.
    const [modalSedes, setModalSedes] = useState(false);
    const history = useHistory();

    // `sede` vem do banco como 1/0
    const ehSede = fornecedor => fornecedor.sede === true || Number(fornecedor.sede) === 1;

    const sedes = fornecedores.filter(ehSede);

    // Sobrou uma sede (ou nenhuma): não há mais o que escolher, e o modal fecha sozinho.
    useEffect(() => {
        if (modalSedes && sedes.length <= 1) setModalSedes(false);
    }, [modalSedes, sedes.length]);

    // Marca ou desmarca a sede na linha. Se depois disso a rede ainda tiver mais de uma sede, abre o
    // modal delas: mais de uma sede na rede é exceção, e é lá que se escolhe quais ficam. Como o
    // resto da tela, só vai ao banco no Salvar.
    const alternarSede = (idFornecedor) => {
        const alvo = fornecedores.find(fornecedor => fornecedor.id === idFornecedor);
        if (!alvo) return;

        const marcar = !ehSede(alvo);
        const atualizados = fornecedores.map(fornecedor => (
            fornecedor.id === idFornecedor ? { ...fornecedor, sede: marcar ? 1 : 0 } : fornecedor
        ));

        setFornecedores(atualizados);
        if (atualizados.filter(ehSede).length > 1) setModalSedes(true);
    };

    // "Desmarcar todas" do modal: sem sede nenhuma, ele fecha sozinho.
    const desmarcarSedes = () => setFornecedores(fornecedores.map(fornecedor => (
        ehSede(fornecedor) ? { ...fornecedor, sede: 0 } : fornecedor
    )));

    const buscar = () => {
        setIsLoading(true);
        // Troca de grupo sem sair da tela (link de outra rede no selo da linha): nada do grupo
        // anterior pode sobrar para o Salvar deste.
        setDados([]);
        setFornecedores([]);
        setErroCarga(false);
        if (id) {
            api({
                method: 'get',
                action: '/fornecedorGrupo/' + id,
                token: token,
            }).then(response => {
                setDados(response.data.data ?? {});
                setFornecedores(response.data.data?.fornecedores ?? []);
            }).catch(() => {
                setErroCarga(true);
                toast.error('Não foi possível carregar o grupo. Recarregue a página.');
            }).finally(() => { setIsLoading(false); });
        } else {
            setIsLoading(false);
        }
    };

    const salvar = () => {
        if (isSubmitting || erroCarga) {
            return;
        }

        setIsSubmitting(true);
        // O back deixa o grupo com exatamente estes fornecedores
        const payload = {
            nome: dados.nome ?? '',
            fornecedores: fornecedores.map(fornecedor => fornecedor.id),
            sedes: fornecedores.filter(ehSede).map(fornecedor => fornecedor.id),
        };
        if (id) {
            api({
                method: 'put',
                action: '/fornecedorGrupo/' + id,
                token: token,
                data: payload
            })
                .then(function () {
                    toast.success("Dados salvos com sucesso!");
                    history.push('/fornecedorGrupo');
                })
                .catch(function () {
                    toast.error('Não foi possível salvar. Tente novamente.');
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        } else {
            api({
                method: 'post',
                action: '/fornecedorGrupo',
                token: token,
                data: payload
            })
                .then(function () {
                    toast.success("Dados salvos com sucesso!");
                    history.push('/fornecedorGrupo');
                })
                .catch(function () {
                    toast.error('Não foi possível salvar. Tente novamente.');
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    // Mesma busca do fornecedor no orçamento: nome, código, nome fantasia ou CNPJ/CPF
    const buscarFornecedores = (input) => api({
        method: 'get',
        action: '/participante',
        token: token,
        params: {
            nome: input,
            tipoCadastro: ['orcamento'],
            situacaoRegistro: 'ativo',
            // os grupos do fornecedor acendem o selo de redes relacionadas quando ele entra na lista
            include: 'fornecedorgrupos',
            limit: 50,
        }
    })
        .then(response => (response.data?.data ?? []).map(registro => ({
            value: registro.id,
            label: (registro.codigo ?? '') + ' - ' + registro.nome
                + (registro.cnpj ? ' (' + registro.cnpj + ')' : registro.cpf ? ' (' + registro.cpf + ')' : ''),
            // quem já está no grupo aparece desabilitado, em vez de sumir da busca
            disabled: fornecedores.some(fornecedor => fornecedor.id === registro.id),
            fornecedor: {
                id: registro.id,
                codigo: registro.codigo,
                nome: registro.nome,
                cnpj: registro.cnpj,
                cpf: registro.cpf,
                situacaoRegistro: registro.situacaoRegistro,
                sede: registro.sede,
                fornecedorgrupos: registro.fornecedorgrupos ?? [],
            },
        })))
        .catch(() => []);

    const fecharAdicionar = () => {
        setSelecionado(null);
        setAdicionando(false);
    };

    const adicionar = () => {
        if (!selecionado) {
            return;
        }
        if (!fornecedores.some(fornecedor => fornecedor.id === selecionado.value)) {
            // mesma ordem do back: a sede primeiro, depois por nome
            setFornecedores([...fornecedores, selecionado.fornecedor]
                .sort((a, b) => (ehSede(b) - ehSede(a)) || (a.nome ?? '').localeCompare(b.nome ?? '')));

            // Entrou uma sede numa rede que já tinha a sua: mesma escolha do marcar na linha.
            if (ehSede(selecionado.fornecedor) && fornecedores.some(ehSede)) setModalSedes(true);
        }
        fecharAdicionar();
    };

    const remover = (idFornecedor) => {
        setFornecedores(fornecedores.filter(fornecedor => fornecedor.id !== idFornecedor));
    };

    // `id` na dependência: o link de outra rede no selo da linha abre o cadastro dela nesta mesma
    // tela, e o React reaproveita o componente em vez de montá-lo de novo.
    useEffect(() => {
        document.querySelector('#nome').focus();
        buscar();
    },
        // eslint-disable-next-line
        [id]);

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div><h1 className="fin-pp__title">Grupo de Fornecedor</h1></div>
                </div>

                <form onSubmit={e => { salvar(); e.preventDefault(); }}>
                    <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
                        <div className="fin-pp__filters">
                            <FinField label="Nome">
                                <FinInput
                                    id="nome"
                                    value={dados.nome || ''}
                                    onChange={e => setDados({ ...dados, nome: e.target.value })}
                                    placeholder="Nome"
                                />
                            </FinField>
                        </div>
                    </div>

                    <div className="fin-pp__region" style={{ marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaUsers size={16} /></span>
                            <div>
                                <h3 className="fin-pp__cphead-title">Fornecedores do grupo</h3>
                                <p className="fin-pp__cphead-sub">{fornecedores.length} fornecedor(es)</p>
                            </div>
                            <AlertaSedes sedes={sedes} onAbrir={() => setModalSedes(true)} />
                            <div className="fin-pp__actions">
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--sm"
                                    onClick={() => setAdicionando(true)}
                                >
                                    <FaPlus size={12} /> Adicionar fornecedor
                                </button>
                                <button
                                    type="submit"
                                    className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                    disabled={isSubmitting || erroCarga}
                                    title={erroCarga ? 'O grupo não carregou. Recarregue a página.' : undefined}
                                >
                                    {isSubmitting
                                        ? <><span className="spinner-border spinner-border-sm me-2" />Salvando...</>
                                        : <><FaSave size={13} /> Salvar</>
                                    }
                                </button>
                            </div>
                        </div>
                        <div className="fin-pp__region-body">
                            <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: 60 }}></th>
                                                <th>Código</th>
                                                <th>Nome</th>
                                                <th>Sede</th>
                                                <th>CNPJ/CPF</th>
                                                <th>Situação</th>
                                                <th style={{ width: 60 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fornecedores.length === 0
                                                ? <tr><td colSpan={7} className="fin-pp__empty">Nenhum fornecedor no grupo</td></tr>
                                                : fornecedores.map(fornecedor => (
                                                    <tr key={fornecedor.id}>
                                                        <td>
                                                            {/* Abre o cadastro do fornecedor, como o lápis do Painel de
                                                                Fornecedores. Mudança não salva do grupo fica para trás. */}
                                                            <Link
                                                                to={'/cadastroFornecedor/' + fornecedor.id}
                                                                className="fin-pp__rowbtn"
                                                                title="Abrir o cadastro do fornecedor"
                                                            >
                                                                <FaPen size={14} />
                                                            </Link>
                                                        </td>
                                                        <td className="fin-pp__cell-muted">{fornecedor.codigo ?? ''}</td>
                                                        <td className="fin-pp__cell-strong">
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                                {fornecedor.nome ?? ''}
                                                                <RedesRelacionadas
                                                                    relacionadas={(fornecedor.fornecedorgrupos ?? []).filter(grupo => String(grupo.id) !== String(id))}
                                                                    titulo="Outras redes deste fornecedor"
                                                                />
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <FinCheck
                                                                id={'sede-' + fornecedor.id}
                                                                title={ehSede(fornecedor) ? 'Desmarcar como sede' : 'Marcar como sede'}
                                                                checked={ehSede(fornecedor)}
                                                                onChange={() => alternarSede(fornecedor.id)}
                                                            />
                                                        </td>
                                                        <td className="fin-pp__cell-muted">{fornecedor.cnpj || fornecedor.cpf || ''}</td>
                                                        <td>
                                                            <span className={`fin-pp__pill ${fornecedor.situacaoRegistro === 'ativo' ? 'is-ok' : 'is-muted'}`}>
                                                                {fornecedor.situacaoRegistro ?? ''}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="fin-pp__rowbtn is-danger"
                                                                title="Remover do grupo"
                                                                onClick={() => remover(fornecedor.id)}
                                                            >
                                                                <FaTrash size={13} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                {modalSedes && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setModalSedes(false); }}
                    >
                        {/* `modal-dialog-scrollable`, como na fila de revisão: rede grande com muitas
                            sedes rola o corpo e mantém o Concluir à vista. */}
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic is-danger"><FaExclamationCircle size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Mais de uma sede nesta rede
                                        <span className="fin-pp__modal-sub">
                                            Desmarque as que não são sede. Com uma ou nenhuma, esta janela fecha sozinha.
                                        </span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={() => setModalSedes(false)}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <FinChecks>
                                        {sedes.map(fornecedor => (
                                            <FinCheck
                                                key={fornecedor.id}
                                                id={'modal-sede-' + fornecedor.id}
                                                label={(fornecedor.nome ?? '') + (fornecedor.cnpj ? ' · ' + fornecedor.cnpj : '')}
                                                checked
                                                onChange={() => alternarSede(fornecedor.id)}
                                            />
                                        ))}
                                    </FinChecks>
                                    {/* `sede` é do fornecedor, não do vínculo com o grupo. */}
                                    <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--fin-mt)' }}>
                                        A sede é marcada no fornecedor: desmarcar aqui vale também para os outros grupos
                                        dele. A mudança é gravada no Salvar do grupo.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={desmarcarSedes}>
                                        Desmarcar todas
                                    </button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary" onClick={() => setModalSedes(false)}>
                                        Concluir
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {adicionando && (
                    <div
                        className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) fecharAdicionar(); }}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaPlus size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Adicionar fornecedor
                                        <span className="fin-pp__modal-sub">{dados.nome || 'Novo grupo'}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={fecharAdicionar}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body" style={{ minHeight: 120 }}>
                                    <div className="fin-pp__filters">
                                        <FinField label="Fornecedor" full>
                                            <ReactSelect
                                                placeholder="Busque por nome, código ou CNPJ"
                                                loadOptions={buscarFornecedores}
                                                value={selecionado}
                                                onChange={setSelecionado}
                                                isClearable
                                            />
                                        </FinField>
                                    </div>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn" onClick={fecharAdicionar}>Cancelar</button>
                                    <button
                                        type="button"
                                        className="fin-pp__btn fin-pp__btn--primary"
                                        disabled={!selecionado}
                                        onClick={adicionar}
                                    >
                                        Adicionar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ErpTemplate>
    );
}
